/**
 * `/admin/family-biz` - spec 10's review surface (FR-7.2).
 *
 * The queue is `?queue=true` on the list route rather than its own path: the
 * queue and the full list are the same query with a different filter. It holds
 * what needs a decision - a submission awaiting review, plus an owner's
 * disable/enable request.
 *
 * Two review states matter and are easy to confuse:
 *
 * - `pending_version_id` - an owner's edit or first submission awaiting review.
 * - `requested_action` - the owner asking to be disabled or re-enabled.
 *
 * The response carries both plus `published` / `pending` / `draft` versions, so
 * a reviewer sees what is live and what is proposed side by side. The panel
 * renders those three versions rather than picking one.
 *
 * `listing_remarks_required` (422) is server-enforced for reject and
 * request-changes: FR-7.2.2 needs a reason the member can act on, and FR-7.2.5
 * pushes it to them.
 */

import { request } from "./client";
import type { DiscountType, ListingStatus, Page } from "./types";

export type ListingLink = {
  platform: string;
  url: string;
  sort_order: number;
};

export type ListingLinkWrite = {
  platform: string;
  url: string;
  sort_order?: number;
};

/** One of up to three listing photos; `slot` (0-2) is its banner position. */
export type ListingPhoto = { slot: number; url: string };

export type AdminVersion = {
  id: string;
  version_no: number;
  state: "draft" | "pending" | "approved" | "rejected" | "superseded";
  business_name: string;
  logo_url: string | null;
  category_id: string | null;
  category_name: string | null;
  description: string | null;
  website_url: string | null;
  phone: string | null;
  email: string | null;
  address: string | null;
  map_url: string | null;
  discount_type: DiscountType | null;
  discount_value: string | null;
  discount_label: string | null;
  terms_and_conditions: string | null;
  show_owner_name: boolean;
  links: ListingLink[];
  photos: ListingPhoto[];
  submitted_at: string | null;
  created_at: string;
  updated_at: string;
};

export type ReviewItem = {
  decision: string;
  remarks: string | null;
  created_at: string;
};

export type ListingAdmin = {
  id: string;
  status: ListingStatus;
  owner_user_id: string;
  owner_name: string | null;
  owner_mobile_number: string | null;
  owner_email: string | null;
  display_order: number;
  is_featured: boolean;
  published_version_id: string | null;
  pending_version_id: string | null;
  submitted_at: string | null;
  decided_at: string | null;
  created_at: string;
  updated_at: string;
  published: AdminVersion | null;
  pending: AdminVersion | null;
  draft: AdminVersion | null;
  latest_remarks: string | null;
  /** The owner's open request, if any. */
  requested_action: "disable" | "enable" | null;
  reviews: ReviewItem[];
};

export type ListingAdminUpdate = {
  business_name?: string;
  category_id?: string | null;
  description?: string;
  website_url?: string;
  phone?: string;
  email?: string;
  address?: string;
  map_url?: string;
  discount_type?: DiscountType;
  discount_value?: string;
  terms_and_conditions?: string;
  links?: ListingLinkWrite[];
  show_owner_name?: boolean;
  display_order?: number;
};

export type ListingOutcome = { listing: ListingAdmin; message: string };
export type MessageOutcome = { message: string };

export type ListingFilters = {
  q?: string;
  owner_q?: string;
  status?: ListingStatus;
  queue?: boolean;
  limit?: number;
  offset?: number;
};

export function fetchListings(filters: ListingFilters): Promise<Page<ListingAdmin>> {
  return request<Page<ListingAdmin>>("/admin/family-biz", { query: { ...filters } });
}

export function fetchListing(listingId: string): Promise<ListingOutcome> {
  return request<ListingOutcome>(`/admin/family-biz/${listingId}`);
}

/** FR-7.2.3 - an admin editing a listing's content, audited as `edited_by_admin`. */
export function updateListing(listingId: string, payload: ListingAdminUpdate): Promise<ListingOutcome> {
  return request<ListingOutcome>(`/admin/family-biz/${listingId}`, {
    method: "PATCH",
    body: payload,
  });
}

/**
 * FR-7.2.2's approve. Remarks are optional - the criterion requires a reason for
 * a refusal, not for a yes - but the route still PARSES a body, so an empty one
 * is sent rather than none.
 */
export function approveListing(listingId: string, remarks?: string): Promise<ListingOutcome> {
  return request<ListingOutcome>(`/admin/family-biz/${listingId}/approve`, {
    method: "POST",
    body: { remarks: remarks ?? null },
  });
}

export function rejectListing(listingId: string, remarks: string): Promise<ListingOutcome> {
  return request<ListingOutcome>(`/admin/family-biz/${listingId}/reject`, {
    method: "POST",
    body: { remarks },
  });
}

export function requestListingChanges(listingId: string, remarks: string): Promise<ListingOutcome> {
  return request<ListingOutcome>(`/admin/family-biz/${listingId}/request-changes`, {
    method: "POST",
    body: { remarks },
  });
}

/** Settle an owner's disable/enable request. `approve=false` refuses it, and then
 *  the remarks are required so the owner knows why. */
export function resolveOwnerRequest(
  listingId: string,
  approve: boolean,
  remarks?: string,
): Promise<ListingOutcome> {
  return request<ListingOutcome>(`/admin/family-biz/${listingId}/resolve-request`, {
    method: "POST",
    body: { approve, remarks: remarks ?? null },
  });
}

export function suspendListing(listingId: string): Promise<ListingOutcome> {
  return request<ListingOutcome>(`/admin/family-biz/${listingId}/suspend`, { method: "POST" });
}

export function unpublishListing(listingId: string): Promise<ListingOutcome> {
  return request<ListingOutcome>(`/admin/family-biz/${listingId}/unpublish`, { method: "POST" });
}

export function reactivateListing(listingId: string): Promise<ListingOutcome> {
  return request<ListingOutcome>(`/admin/family-biz/${listingId}/reactivate`, { method: "POST" });
}

export async function uploadListingLogo(
  listingId: string,
  file: File,
): Promise<{ logo_url: string; message: string }> {
  const body = new FormData();
  body.append("file", file);
  return request(`/admin/family-biz/${listingId}/logo`, { method: "POST", body });
}
